// src/pages/PersonalDashboard.jsx

import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import useTabCounts from "../hooks/useTabCounts";
import "./PersonalDashboard.css";
import { 
    FiUser, FiCalendar, FiBriefcase, FiCheckSquare, 
    FiMessageSquare, FiTrendingUp, FiDollarSign
} from "react-icons/fi";

import EmployeeProfile from "./EmployeeProfile";
import Attendance from "./Attendance";
import Leave from "./Leave";
import Tasks from "./Tasks";
import ChatRoom from "./ChatRoom";
import Performance from "./Performance";
import Salary from "./Salary";

const PersonalDashboard = () => {
    const { userData, loading: authLoading } = useAuth();
    const [activeTab, setActiveTab] = useState("profile");
    const tabCounts = useTabCounts(userData?.uid);

    // Remove dashboard-content padding so pd-header sits flush against the navbar
    useEffect(() => {
        const el = document.querySelector('.dashboard-content');
        if (!el) return;
        const prevPadding = el.style.padding;
        el.style.padding = '0';
        return () => {
            el.style.padding = prevPadding;
        };
    }, []);

    if (authLoading) {
        return <div className="loading-state">Loading Your Dashboard...</div>;
    }

    if (!userData) {
        return <div className="error-state">Could not load user data.</div>;
    }

    const dashboardFeatures = [
        { id: "profile",     name: "Profile",     icon: <FiUser size={16} /> },
        { id: "attendance",  name: "Attendance",  icon: <FiCalendar size={16} /> },
        { id: "leaves",      name: "Leaves",      icon: <FiBriefcase size={16} /> },
        { id: "tasks",       name: "Tasks",        icon: <FiCheckSquare size={16} /> },
        { id: "chat",        name: "Chat",         icon: <FiMessageSquare size={16} /> },
        { id: "performance", name: "Performance", icon: <FiTrendingUp size={16} /> },
        { id: "salary",      name: "Payroll",     icon: <FiDollarSign size={16} /> },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case "profile":     return <EmployeeProfile employee={userData} />;
            case "attendance":  return <Attendance />;
            case "leaves":      return <Leave />;
            case "tasks":       return <Tasks />;
            case "chat":        return <ChatRoom />;
            case "performance": return <Performance />;
            case "salary":      return <Salary />;
            default:            return <EmployeeProfile employee={userData} />;
        }
    };

    const activeFeature = dashboardFeatures.find(f => f.id === activeTab);

    return (
        <div className="pd-shell">
            {/* ── Top Navigation Header ── */}
            <header className="pd-header">
                <div className="pd-header-brand">
                    <span className="pd-header-icon-wrap">
                        <FiUser size={18} />
                    </span>
                    <div className="pd-header-text">
                        <span className="pd-header-title">My Portal</span>
                        <span className="pd-header-subtitle">{userData.name}</span>
                        <span className="pd-header-subtitle pd-header-dept">
                            ({userData.department || userData.role})
                        </span>
                    </div>
                </div>

                {/* Desktop tab nav */}
                <nav className="pd-tab-nav" aria-label="Portal navigation">
                    {dashboardFeatures.map((item) => {
                        const count = tabCounts[item.id] || 0;
                        return (
                            <button
                                key={item.id}
                                className={`pd-tab-btn ${activeTab === item.id ? "pd-tab-btn--active" : ""}`}
                                onClick={() => setActiveTab(item.id)}
                                aria-current={activeTab === item.id ? "page" : undefined}
                            >
                                <span className="pd-tab-icon">{item.icon}</span>
                                <span className="pd-tab-label">{item.name}</span>
                                {count > 0 && (
                                    <span className="pd-tab-badge">{count > 99 ? '99+' : count}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Mobile select */}
                <div className="pd-mobile-select-wrap">
                    <label htmlFor="pd-mobile-select" className="sr-only">Navigation</label>
                    <select
                        id="pd-mobile-select"
                        className="pd-mobile-select"
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                    >
                        {dashboardFeatures.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                    </select>
                </div>
            </header>

            {/* ── Content Area ── */}
            <main className="pd-content-area">
                <div className="pd-content-inner">
                    {renderContent()}
                </div>
            </main>
        </div>
    );
};

export default PersonalDashboard;